import type { Brick } from "../store/slices/bricksSlice";
import type { WallTileLayout } from "./layout";

export interface RasterizeOptions {
  pxPerMm: number;
  jointImage: CanvasImageSource & { width: number; height: number };
  jointTextureSizeMm: number;
  brickImage(brick: Brick): CanvasImageSource;
}

export interface RasterizedTile {
  width: number;
  height: number;
  pxPerMm: number;
  albedo: Uint8ClampedArray;
  mask: Uint8Array;
}

const context2d = (width: number, height: number) => {
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("2D canvas is not available");
  return { canvas, ctx };
};

export const rasterizeTile = (
  layout: WallTileLayout,
  { pxPerMm, jointImage, jointTextureSizeMm, brickImage }: RasterizeOptions
): RasterizedTile => {
  const width = Math.round(layout.widthMm * pxPerMm);
  const height = Math.round(layout.heightMm * pxPerMm);
  const sx = width / layout.widthMm;
  const sy = height / layout.heightMm;

  const bricks = context2d(width, height);
  bricks.ctx.imageSmoothingQuality = "high";
  for (const b of layout.bricks) {
    const img = brickImage(b.brick);
    // Draw once more one tile width away where a brick crosses the tile edge.
    for (const offset of [-layout.widthMm, 0, layout.widthMm]) {
      const x = b.x + offset;
      if (x + b.width <= 0 || x >= layout.widthMm) continue;
      bricks.ctx.save();
      bricks.ctx.setTransform(
        b.mirrored ? -sx : sx,
        0,
        0,
        sy,
        (b.mirrored ? x + b.width : x) * sx,
        b.y * sy
      );
      bricks.ctx.drawImage(img, 0, 0, b.width, b.height);
      bricks.ctx.restore();
    }
  }
  const brickPixels = bricks.ctx.getImageData(0, 0, width, height).data;

  // Whole repeats per tile keep the joint texture seamless, at the cost of a
  // slight stretch away from its nominal size.
  const repeatsX = Math.max(1, Math.round(layout.widthMm / jointTextureSizeMm));
  const repeatsY = Math.max(1, Math.round(layout.heightMm / jointTextureSizeMm));
  const albedo = context2d(width, height);
  const pattern = albedo.ctx.createPattern(jointImage, "repeat");
  if (!pattern) throw new Error("Could not create joint pattern");
  pattern.setTransform(
    new DOMMatrix().scaleSelf(
      width / repeatsX / jointImage.width,
      height / repeatsY / jointImage.height
    )
  );
  albedo.ctx.fillStyle = pattern;
  albedo.ctx.fillRect(0, 0, width, height);
  albedo.ctx.drawImage(bricks.canvas, 0, 0);
  const albedoPixels = albedo.ctx.getImageData(0, 0, width, height).data;

  const mask = new Uint8Array(width * height);
  for (let i = 0; i < mask.length; i++) mask[i] = brickPixels[i * 4 + 3];

  return { width, height, pxPerMm: sx, albedo: albedoPixels, mask };
};

export const toCanvas = (width: number, height: number, rgba: Uint8ClampedArray) => {
  const { canvas, ctx } = context2d(width, height);
  ctx.putImageData(new ImageData(rgba as Uint8ClampedArray<ArrayBuffer>, width, height), 0, 0);
  return canvas;
};
